import React, { useState } from 'react';
import { Card, Radio, Empty, Row, Col, Space, Typography } from 'antd';
import {
  LineChart,
  Line,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  LineChartOutlined,
  UserAddOutlined,
  DollarOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import { UserAnalytics } from 'types/push.types';
import { AppStatsCard } from './AppStatsCard';
import { ChartTooltip } from './ChartTooltip';
import { useChartTheme } from './chartTheme';
import styles from '../../pages/Statistics/statistics.module.css';

const { Text } = Typography;

interface UserAnalyticsChartProps {
  data: {
    app_id: number;
    period: string;
    group_by: string;
    analytics: UserAnalytics[] | null;
  } | null;
}

const SERIES = [
  { key: 'installs', name: 'Установки' },
  { key: 'registrations', name: 'Регистрации' },
  { key: 'deposits', name: 'Депозиты' },
];

const groupByLabels: Record<string, string> = {
  day: 'По дням',
  week: 'По неделям',
  month: 'По месяцам',
};

const formatPeriod = (period: string) => {
  if (!period) return '';
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

export const UserAnalyticsChart: React.FC<UserAnalyticsChartProps> = ({ data }) => {
  const [chartType, setChartType] = useState<'line' | 'area'>('area');
  const chart = useChartTheme();

  const analytics = data?.analytics ?? [];

  const totals = analytics.reduce(
    (acc, item) => ({
      installs: acc.installs + (item.installs ?? 0),
      registrations: acc.registrations + (item.registrations ?? 0),
      deposits: acc.deposits + (item.deposits ?? 0),
    }),
    { installs: 0, registrations: 0, deposits: 0 },
  );

  const formattedData = analytics.map((item) => ({
    ...item,
    period: formatPeriod(item.period),
  }));

  return (
    <Card
      title={
        <span className={styles.chartTitle}>
          <div className={styles.chartTitleIcon}>
            <LineChartOutlined />
          </div>
          Динамика активности
        </span>
      }
      extra={
        <Radio.Group
          value={chartType}
          onChange={(event) => setChartType(event.target.value)}
          size="small"
        >
          <Radio.Button value="area">Область</Radio.Button>
          <Radio.Button value="line">Линии</Radio.Button>
        </Radio.Group>
      }
      className={styles.statsCard}
    >
      {analytics.length > 0 ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8}>
              <AppStatsCard
                title="Установки"
                value={totals.installs}
                icon={<AppstoreAddOutlined />}
                className={styles.info}
                compact
              />
            </Col>
            <Col xs={24} sm={8}>
              <AppStatsCard
                title="Регистрации"
                value={totals.registrations}
                icon={<UserAddOutlined />}
                className={styles.success}
                compact
              />
            </Col>
            <Col xs={24} sm={8}>
              <AppStatsCard
                title="Депозиты"
                value={totals.deposits}
                icon={<DollarOutlined />}
                compact
              />
            </Col>
          </Row>

          <ResponsiveContainer width="100%" height={300}>
            {chartType === 'area' ? (
              <AreaChart data={formattedData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="period"
                  tick={chart.tick}
                  tickLine={false}
                  axisLine={{ stroke: chart.grid }}
                  minTickGap={16}
                />
                <YAxis tick={chart.tick} tickLine={false} axisLine={false} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ paddingTop: 8, fontSize: 12 }} iconType="circle" iconSize={8} />
                {SERIES.map((series, index) => (
                  <Area
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.name}
                    stroke={chart.series[index]}
                    fill={chart.series[index]}
                    fillOpacity={0.14}
                    strokeWidth={2}
                  />
                ))}
              </AreaChart>
            ) : (
              <LineChart data={formattedData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="period"
                  tick={chart.tick}
                  tickLine={false}
                  axisLine={{ stroke: chart.grid }}
                  minTickGap={16}
                />
                <YAxis tick={chart.tick} tickLine={false} axisLine={false} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ paddingTop: 8, fontSize: 12 }} iconType="circle" iconSize={8} />
                {SERIES.map((series, index) => (
                  <Line
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.name}
                    stroke={chart.series[index]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            )}
          </ResponsiveContainer>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Группировка: {groupByLabels[data?.group_by ?? 'day'] ?? 'По дням'}
          </Text>
        </Space>
      ) : (
        <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Card>
  );
};